import React from "react";
// import ReactDOM from 'react-dom';
import { createRoot } from "react-dom/client";
import App from "./App";
import * as serviceWorker from "./serviceWorker";
import { Provider } from "react-redux";
import { PersistGate } from "redux-persist/integration/react";
import axios from "axios";
import { store, persistor } from "./store";
import AppLoader from "./_common/components/elements/full-page-loader/appLoader";
import { ACTIONS, API_URL, APP_STAGE, REFRESH_STORAGE, SAGA_ACTIONS } from "./_config";
import { getUserTimezone } from "./_config/functions";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "react-confirm-alert/src/react-confirm-alert.css";
import "react-datepicker/dist/react-datepicker.css";
import { setupTokenBroadcastListener, requestTokenFromExistingTab } from "./_common/utils/tokenBroadcast";
import { captureAdsAttributionFromUrl } from "./_common/utils/adsAttribution";

captureAdsAttributionFromUrl();
// import { SocketProvider } from "./_common/hooks/custom/SocketContext";

// Create root once and reuse it
const container = document.getElementById("root");
const root = createRoot(container!);

function render() {
  root.render(
    <React.Fragment>
      <Provider store={store}>
        <PersistGate loading={null} persistor={persistor}>
          {/* <SocketProvider> */}
          <React.Fragment>
            <AppLoader />
            <App />
            <ToastContainer
              position="top-right"
              autoClose={5000}
              hideProgressBar={false}
              newestOnTop={true}
              closeOnClick
              rtl={false}
              pauseOnFocusLoss
              draggable
              pauseOnHover
              style={{ zIndex: 99999 }}
            />
          </React.Fragment>
          {/* </SocketProvider> */}
        </PersistGate>
      </Provider>
    </React.Fragment>,
  );
}

// Set up listener immediately — this tab will respond to token requests from new tabs,
// and will log itself out if another tab broadcasts a force-logout (e.g. AutoLogin).
setupTokenBroadcastListener(() => {
  sessionStorage.removeItem(REFRESH_STORAGE);
  localStorage.clear();
  store.dispatch({ type: ACTIONS.USER.LOGOUT });
});

async function initialize() {
  // Refresh token lives in sessionStorage (tab-isolated, cleared when all tabs close).
  // If this tab has no token, ask other open tabs via BroadcastChannel.
  let refreshToken = sessionStorage.getItem(REFRESH_STORAGE);

  if (!refreshToken) {
    // Check localStorage — refresh token is mirrored there on login for cross-tab access.
    const localToken = localStorage.getItem(REFRESH_STORAGE);
    if (localToken) {
      sessionStorage.setItem(REFRESH_STORAGE, localToken);
      refreshToken = localToken;
    }
  }

  if (!refreshToken) {
    // No token in this tab — ask other open tabs (waits up to 500ms).
    refreshToken = await requestTokenFromExistingTab();
    if (refreshToken) {
      sessionStorage.setItem(REFRESH_STORAGE, refreshToken);
    }
  }

  if (refreshToken) {
    // Call /auth/refresh with the refresh token in the POST body.
    // The backend validates it and returns a fresh access token in the JSON response.
    // The access token is stored in Redux memory only — never in browser storage.
    try {
      const success = await axios({
        method: "POST",
        url: API_URL.USER.REFRESH,
        headers: { "X-Timezone": getUserTimezone() },
        data: { refreshToken },
      });

      // /auth/refresh returns { status, message, data: { token, token_type } }
      const token = success.data?.data?.token;

      if (token) {
        // Access token lives in Redux memory only — never written to browser storage.
        // User data is already rehydrated from redux-persist (token was stripped but user persists).
        const persistedUser = store.getState().user?.user;

        store.dispatch({
          type: ACTIONS.USER.LOGIN,
          payload: { user: persistedUser, token },
        });

        if (persistedUser?.user_type === "member" && persistedUser?.id) {
          store.dispatch({
            type: SAGA_ACTIONS.USER.GET_PERMISSIONS,
            payload: { userId: persistedUser.id },
            callbackSuccess: () => {},
            callbackError: (error: any) => {
              console.warn("Failed to load user permissions on app initialization:", error);
            },
          });
        }
      } else {
        // Refresh succeeded but response was malformed — treat as auth failure.
        sessionStorage.removeItem(REFRESH_STORAGE);
        localStorage.removeItem(REFRESH_STORAGE);
        store.dispatch({ type: ACTIONS.USER.LOGOUT });
      }
    } catch {
      sessionStorage.removeItem(REFRESH_STORAGE);
      localStorage.removeItem(REFRESH_STORAGE);
      store.dispatch({ type: ACTIONS.USER.LOGOUT });
    }
  } else {
    // No refresh token in this tab or any other tab — route guards redirect to login.
    store.dispatch({ type: ACTIONS.USER.LOGOUT });
  }

  render();
}

initialize();

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
if (APP_STAGE === "prod") {
  serviceWorker.register();
} else {
  serviceWorker.unregister();
}
